import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Thermometer,
  Droplets,
  Zap,
  Wind,
  Snowflake,
  Fan,
  Flame,
  ShowerHead,
  Home,
  type LucideIcon,
} from 'lucide-react';
import { SectionWrapper, SectionHeader } from '@/components/common';
import { SERVICE_TYPE_LABELS, type ServiceType } from '@/types';

// Service type configuration with icons and colors
const SERVICE_CONFIG: Record<
  ServiceType,
  {
    icon: LucideIcon;
    color: string;
    lightBg: string;
  }
> = {
  hvac: { icon: Thermometer, color: 'text-blue-600', lightBg: 'bg-blue-50' },
  plumbing: { icon: Droplets, color: 'text-cyan-600', lightBg: 'bg-cyan-50' },
  electrical: { icon: Zap, color: 'text-yellow-600', lightBg: 'bg-yellow-50' },
  heating: { icon: Flame, color: 'text-orange-600', lightBg: 'bg-orange-50' },
  cooling: { icon: Snowflake, color: 'text-sky-600', lightBg: 'bg-sky-50' },
  ventilation: { icon: Fan, color: 'text-teal-600', lightBg: 'bg-teal-50' },
  refrigeration: { icon: Snowflake, color: 'text-indigo-600', lightBg: 'bg-indigo-50' },
  water_heater: { icon: ShowerHead, color: 'text-red-600', lightBg: 'bg-red-50' },
  duct_cleaning: { icon: Wind, color: 'text-gray-600', lightBg: 'bg-gray-50' },
  insulation: { icon: Home, color: 'text-green-600', lightBg: 'bg-green-50' },
};

interface OtherServicesGridProps {
  title?: string;
  subtitle?: string;
  excludeService?: ServiceType;
  limit?: number;
  className?: string;
}

export function OtherServicesGrid({
  title,
  subtitle,
  excludeService,
  limit = 5,
  className,
}: OtherServicesGridProps) {
  const { t } = useTranslation('services');

  const displayTitle = title || t('otherServices.title');
  const displaySubtitle = subtitle || t('otherServices.subtitle');

  const services = Object.entries(SERVICE_CONFIG)
    .filter(([key]) => key !== excludeService)
    .slice(0, limit);

  return (
    <SectionWrapper bgColor="bg-white" className={className}>
      <SectionHeader
        title={displayTitle}
        subtitle={displaySubtitle}
        className="mb-12"
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {services.map(([key, cfg]) => {
          const ServiceIcon = cfg.icon;
          return (
            <Link
              key={key}
              to={`/services/${key}`}
              className="group p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all text-center"
            >
              <div className={`w-12 h-12 mx-auto mb-3 ${cfg.lightBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <ServiceIcon className={`w-6 h-6 ${cfg.color}`} />
              </div>
              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                {SERVICE_TYPE_LABELS[key as ServiceType]}
              </span>
            </Link>
          );
        })}
      </div>
    </SectionWrapper>
  );
}
